import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../utils/auth";
import LoadingScreen from "../components/LoadingScreen";
import { Dialog, Listbox, RadioGroup, Transition } from "@headlessui/react";

import { ReactComponent as Chevron } from "../assets/icons/Chevron.svg";
import { cndURL, sdkWrapperURL } from "../utils/api-url-list";
import { ReactComponent as Check } from "../assets/icons/Check.svg";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

const RequestAppointment = () => {
  const {
    getToken,
    userData: { profile_id, first_name, last_name },
  } = useAuth();
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const IST = "Asia/Kolkata";
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [careteamList, setCareteamList] = useState([]);
  const [selectedMemberID, setSelectedMemberID] = useState("");
  const [selectedDate, setSelectedDate] = useState(() =>
    dayjs().tz(IST).startOf("day")
  );
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedEndTime, setSelectedEndTime] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [slotsError, setSlotsError] = useState("");

  // Function to detect Safari
  function isSafari() {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  const getSlots = async (date, id) => {
    setSlotsError("");
    setLoading(true);
    try {
      const slotResp = await fetch(cndURL("/cnd/public/calendar-slots"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          appointment_date: date,
          appointment_type: "2",
          doctor_id: id,
          visit_type: "1",
        }),
      });
      const slotRespJSON = await slotResp.json();
      if (slotRespJSON?.status?.toString().startsWith("2")) {
        const now = new Date(); // Get the current time
        const filteredSlots = slotRespJSON?.slot_data?.filter(
          (slot) => new Date(slot.appointment_start_time) > now
        );
        if (filteredSlots.length === 0) {
          setSlotsError("Slots does not exist.");
        } else {
          setTimeSlots(filteredSlots);
        }
      } else throw new Error(slotRespJSON?.message ?? "Slots does not exist.");
    } catch (err) {
      console.error(err);
      setSlotsError("Slots does not exist.");
      setTimeSlots([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const token = await getToken();
        const dataResp = await fetch(
          sdkWrapperURL("/users/get-doctor-nurse-info"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
            body: JSON.stringify({ pd_id: profile_id }),
          }
        );
        const dataRespJSON = await dataResp.json();
        if (dataRespJSON?.statusCode?.toString().startsWith("2")) {
          const { doctor, nurse, health_coach } = dataRespJSON;
          const updatedCareteamList = [
            { id: doctor.id, name: `${doctor.first_name} ${doctor.last_name}` },
            { id: nurse.id, name: `${nurse.first_name} ${nurse.last_name}` },
            ...(health_coach && Object.keys(health_coach).length > 0
              ? [
                  {
                    id: health_coach.id,
                    name: `${health_coach.first_name} ${health_coach.last_name}`,
                  },
                ]
              : []),
            { id: "OTHER-ID", name: "Other" },
          ];
          setCareteamList(updatedCareteamList);
          setSelectedMemberID(doctor.id);
          const todayDate = dayjs().tz(IST).format("YYYY-MM-DD");
          setSelectedDate(todayDate);
          await getSlots(todayDate, doctor.id);
        } else throw new Error(dataRespJSON.message);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [getToken, profile_id]);

  const chooseSlot = (slot) => {
    setSelectedTime(slot.appointment_start_time);
    setSelectedEndTime(slot.appointment_end_time);
    setModalOpen(true);
  };
  const updatdTime = timeSlots.map((slot, index) => ({
    id: slot.id,
  }));

  useEffect(() => {
    if (selectedMemberID === "OTHER-ID") {
      setModalOpen(true);
    }
  }, [selectedMemberID]);

  const register = async () => {
    setIsLoading(true);
    try {
      if (selectedMemberID === "OTHER-ID") {
        if (reason.trim().length > 0) {
          // api call for other
          const token = await getToken();
          const registerResp = await fetch(
            sdkWrapperURL("/users/request-appointment"),
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: token,
              },
              body: JSON.stringify({
                appointment_with: "other",
                reason: reason,
                appointment_type: "2",
                visit_type: "1",
                appointment_date: selectedDate,
                address: "",
                state_id: "",
                city_id: "",
                pincode: "",
                booked_by_first_name: first_name,
                booked_by_last_name: last_name,
                username: "",
              }),
            }
          );
          const registerRespJSON = await registerResp.json();
          if (registerRespJSON?.statusCode?.toString().startsWith("2")) {
            setSuccessModalOpen(true);
            setModalOpen(false);
            setReason("");
            setReasonError("");
          }
        } else {
          setReasonError("Reason is Required.");
          return;
        }
      } else {
        if (reason.trim().length > 0) {
          const token = await getToken();
          const registerResp = await fetch(
            sdkWrapperURL("/users/request-appointment"),
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: token,
              },
              body: JSON.stringify({
                doctor_id: selectedMemberID,
                appointment_with: careteamList.find(
                  (c) => c.id === selectedMemberID
                )?.name,
                reason: reason,
                appointment_type: "2",
                visit_type: "1",
                appointment_date: selectedDate,
                start_time: isSafari
                  ? new Date(selectedTime.replace(" ", "T") + "Z")
                      .toISOString()
                      .split("T")[1]
                      .slice(0, 8) + "+00:00"
                  : new Date(selectedTime + " UTC")
                      .toISOString()
                      .split("T")[1]
                      .slice(0, 8) + "+00:00",
                end_time: isSafari
                  ? new Date(selectedEndTime.replace(" ", "T") + "Z")
                      .toISOString()
                      .split("T")[1]
                      .slice(0, 8) + "+00:00"
                  : new Date(selectedEndTime + " UTC")
                      .toISOString()
                      .split("T")[1]
                      .slice(0, 8) + "+00:00",
                start_time_slot: selectedTime,
                end_time_slot: selectedEndTime,
                appointment_start_time: new Date(selectedTime)
                  .toTimeString()
                  .split(" ")[0],
                appointment_end_time: new Date(selectedEndTime)
                  .toTimeString()
                  .split(" ")[0],
                slot_id: updatdTime[0].id ? updatdTime[0].id : "",
                address: "",
                state_id: "",
                city_id: "",
                pincode: "",
                booked_by_first_name: first_name,
                booked_by_last_name: last_name,
                username: "",
              }),
            }
          );
          const registerRespJSON = await registerResp.json();
          if (registerRespJSON?.statusCode?.toString().startsWith("2")) {
            setModalOpen(false);
            setSuccessModalOpen(true);
            setSelectedTime("");
            setReason("");
            setReasonError("");
          }
        } else {
          setReasonError("Reason is Required.");
          return;
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return loading ? (
    <LoadingScreen style={{ minHeight: "60vh" }} />
  ) : (
    <div>
      <Listbox
        as="div"
        className="mt-5 relative "
        value={selectedMemberID}
        onChange={(mID) => {
          getSlots(selectedDate, mID);
          setSelectedMemberID(mID);
        }}
      >
        <Listbox.Label className="text-xs">Appointment With</Listbox.Label>
        <Listbox.Button className="w-full flex items-center justify-between space-x-4 mt-2 px-4 py-2.5 rounded-lg outline-none border-2 focus:border-secondary disabled:border-[#BABABA] bg-white disabled:bg-[#BABABA] text-primary text-xs text-left group">
          <span>
            {careteamList.find((c) => c.id === selectedMemberID)?.name}
          </span>
          <Chevron className="shrink-0 h-3.5 w-3.5 text-secondary group-disabled:hidden" />
        </Listbox.Button>
        <Transition
          // as={Fragment}
          enter="transition duration-200 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-300 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <Listbox.Options className="absolute w-full mt-1 py-1 shadow-lg rounded-md bg-white text-primary text-xs">
            {careteamList.map((c, idx) => (
              <Listbox.Option
                key={`selected-${idx}`}
                value={c.id}
                className="px-4 py-2 active:bg-secondary active:text-white"
              >
                {c.name}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </Listbox>
      <RadioGroup
        value={selectedDate}
        onChange={(dt) => {
          getSlots(dt, selectedMemberID);
          setSelectedDate(dt);
        }}
        className="mt-5 flex items-stretch space-x-3 overflow-x-auto no-scrollbar"
      >
        {Array.from({ length: 7 }, (_, i) => {
          const istDay = dayjs().tz(IST).add(i, "day");
          return istDay.format("YYYY-MM-DD");
        }).map((day, index) => (
          <RadioGroup.Option
            value={day}
            className={({ checked }) =>
              `shrink-0 w-2/5 px-4 py-2 outline-none rounded-xl bg-gradient-to-b cursor-pointer ${
                checked ? "from-secondary" : "from-[#44148B]"
              } to-transparent text-white text-sm font-light`
            }
          >
            {dayjs(day).tz(IST).format("dddd")}
            <br />
            {dayjs(day).tz(IST).format("MMM DD")}
          </RadioGroup.Option>
        ))}
      </RadioGroup>
      <div className="m-4 text-center text-lg font-semibold text-white">
        {dayjs(`${selectedDate}T00:00:00`).tz(IST).format("dddd, MMM DD")}
      </div>
      {slotsError ? (
        <p className="mt-10 text-white mx-auto text-center font-semibold">
          {slotsError}
        </p>
      ) : (
        selectedDate !== null && (
          <div className="grid grid-cols-3 gap-4 mt-6">
            {timeSlots.map((slot, index) => (
              <div
                key={index}
                onClick={() => chooseSlot(slot)}
                className={`shrink-0  flex flex-col py-2 px-4 outline-none rounded-xl bg-gradient-to-b cursor-pointer ${
                  selectedTime === slot.appointment_start_time
                    ? "from-secondary"
                    : "from-[#44148B]"
                } to-transparent text-white text-sm`}
              >
                {new Date(slot.appointment_start_time).toLocaleTimeString(
                  undefined,
                  {
                    hour12: true,
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                )}
              </div>
            ))}
          </div>
        )
      )}
      {/* Register Modal */}
      <Transition show={isModalOpen} as={Fragment}>
        <Dialog
          open={isModalOpen}
          onClose={() => setModalOpen(false)}
          className="relative z-50"
          as={Fragment}
        >
          <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
            <Transition.Child
              className="fixed inset-0 bg-black/70 flex items-center justify-center"
              enter="duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="duration-300 delay-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Transition.Child
                as={Fragment}
                enter="delay-150 duration-200"
                enterFrom="opacity-0 scale-50"
                enterTo="opacity-100 scale-100"
                leave="duration-100"
                leaveFrom="opacity-50 scale-50"
                leaveTo="opacity-0 scale-0"
              >
                <Dialog.Panel className="w-11/12 bg-white rounded-xl p-5 flex flex-col text-center">
                  <Dialog.Title className="text-left font-semibold">
                    Appointment Details:
                  </Dialog.Title>
                  {selectedMemberID !== "OTHER-ID" && (
                    <Dialog.Description className="mt-4 flex justify-between">
                      <div>
                        <p className="text-black text-xs font-normal">
                          Appointment With:
                        </p>
                        <p className="text-secondary text-xs font-light">
                          {
                            careteamList.find((c) => c.id === selectedMemberID)
                              ?.name
                          }
                        </p>
                      </div>
                      <div>
                        <p className="text-black text-xs font-normal">
                          Date and Time:
                        </p>
                        <p className="text-secondary  text-xs font-light">
                          {new Date(`${selectedTime}`).toLocaleString(
                            undefined,
                            {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                              hour12: true,
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </p>
                      </div>
                    </Dialog.Description>
                  )}
                  <label htmlFor="reason" className="text-xs text-left my-2 ">
                    Reason of appointment
                    <span className="text-error">&nbsp;*</span>
                  </label>
                  <textarea
                    id="reason"
                    name="reason"
                    rows="4"
                    className="block p-2.5 w-full text-[11px] text-primary bg-white rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 placeholder:text-[#5C6984]/[.6]"
                    placeholder="Reason of appointment"
                    required
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  {reasonError?.length > 0 && (
                    <p className="mt-1.5 text-xxs text-error">{reasonError}</p>
                  )}
                  <div className="flex gap-4">
                    <button
                      onClick={() => {
                        const defaultMemID = careteamList[0].id;
                        getSlots(selectedDate, defaultMemID);
                        setSelectedMemberID(defaultMemID);
                        setReason("");
                        setReasonError("");
                        setModalOpen(false);
                        setSelectedTime("");
                      }}
                      className="flex items-center justify-center space-x-2 mt-6 w-full rounded-full outline-none px-5 py-2.5 bg-gradient disabled:bg-secondary text-white"
                    >
                      Cancel
                    </button>
                    <button
                      disabled={isLoading}
                      onClick={register}
                      className={`flex items-center justify-center space-x-2 mt-6 w-full rounded-full outline-none px-5 py-2.5 ${
                        isLoading ? "bg-gray-600" : "bg-gradient"
                      } text-white`}
                    >
                      Request
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
      {/* Success Modal */}
      <Dialog
        open={isSuccessModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <Dialog.Panel className=" bg-white rounded-xl p-6 flex flex-col text-center">
            <Check className="shrink-0 h-24 w-24 p-4 mx-auto mb-6 rounded-full border-4 border-current text-success" />
            <Dialog.Description>
              Your Registration is successfully completed
            </Dialog.Description>
            <button
              onClick={() => setSuccessModalOpen(false)}
              className="flex items-center justify-center space-x-2 mt-6 w-full rounded-full outline-none px-5 py-2.5 bg-gradient disabled:bg-secondary text-white"
            >
              OK
            </button>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default RequestAppointment;
